import { useIdle } from "@mantine/hooks"
import { LoginError } from "@models/error/login-error"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	BASE_LEGACY_URL_LOGOUT,
	IDLE_TIMEOUT_SECONDS,
	PENDING,
	USE_FORGEROCK
} from "@utils/constants.util"
import { AppEvents, StorageKeys } from "@utils/user-config"
import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useRef,
	useCallback
} from "react"
import {
	DismissedNotificationRequest,
	UserProfile
} from "../models/userProfile.model"
import { AuthService } from "../services/authService"
import { ForgeRockAuthService } from "@services/forgeRockAuthService"
import { useUserStore } from "../hooks/useUserStore"
import { useAppShellContext } from "./AppShellProvider"
import { useNavigate } from "react-router-dom"
import secureLocalStorage from "react-secure-storage"
import { useRedirect } from "@hooks/useRedirect"
import { createDismissedNotification } from "@services/dismissedNotificationsService"
import { adaptForgeRockUser } from "@services/userProfileAdapter"
import oauthService, { UserInfo } from "@services/oauthService"
import { getLocaleStringFromUserProfile } from "@hooks/useLocale"
import { initializeCsrf } from "@services/csrfService"

export interface AuthContextProps {
	userProfile: UserProfile | null
	setUserProfile: (userProfile: UserProfile) => void
	isAuthenticated: boolean
	isLoggingOut: boolean
	isLoading: boolean
	login: (credentials: { username: string; password: string }) => Promise<{
		token: string
		userProfile: UserProfile
	}>
	postLogin: () => Promise<{ userProfile: UserProfile }>
	logout: () => void
	error: LoginError
	dismissNotification: (
		notification: DismissedNotificationRequest
	) => Promise<void>
	refreshUserProfile: () => Promise<UserProfile>
	refreshSession: (token: string, idToken?: string) => Promise<void>
	forceRefreshToken: () => Promise<void>
	loginWithForgeRock?: (userInfo: UserInfo) => Promise<void>
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

export const useAuthContext = (): AuthContextProps => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error("useAuthContext must be used within AuthProvider")
	}
	return context
}

interface AuthProviderProps {
	children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<LoginError | null>(null)
	const setUserProfile = useUserStore((state) => state.setUserProfile)
	const userProfile = useUserStore((state) => state.userProfile)
	const idleTimerRef = useRef<number>(0)
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const isLoggingOutRef = useRef<boolean>(false)
	const { handleRedirect } = useRedirect()
	const { closeMenu } = useAppShellContext()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const loginMutation = useMutation({
		mutationFn: async (credentials: { username: string; password: string }) => {
			const { token, id_token } = await AuthService.login(
				credentials.username,
				credentials.password
			)

			if (USE_FORGEROCK) {
				AuthService.setAuthToken(token, id_token || "")
			} else {
				AuthService.setAuthToken(token)
			}

			// The csrf-token endpoint is authenticated, so fetch the tokens
			// only after the auth token has been set.
			await initializeCsrf()

			const userProfile = await AuthService.fetchProfile()

			const next = getLocaleStringFromUserProfile(userProfile)
			console.log("lang from user profile: ", next)
			if (next) {
				secureLocalStorage.setItem(StorageKeys.USER_PREFERRED_LANGUAGE, next)
				window.dispatchEvent(
					new CustomEvent(AppEvents.USER_LANGUAGE_CHANGED, {
						detail: { locale: next }
					})
				)
			}

			return { token, id_token, userProfile }
		},
		onSuccess: ({ userProfile }) => {
			setUserProfile(userProfile)
			setIsAuthenticated(true)
			setError(null)
			isLoggingOutRef.current = false
		},
		onError: (error: LoginError) => {
			setError(error)
		}
	})

	const postLoginMutation = useMutation({
		mutationFn: async () => {
			const token = AuthService.getAuthToken()
			const id_token = AuthService.getIdToken()

			if (USE_FORGEROCK) {
				AuthService.setAuthToken(token, id_token || "")
			} else {
				AuthService.setAuthToken(token)
			}

			// The csrf-token endpoint is authenticated, so fetch the tokens
			// only after the auth token has been set.
			await initializeCsrf()

			const userProfile = await AuthService.fetchProfile()
			return { userProfile }
		},
		onSuccess: ({ userProfile }) => {
			setUserProfile(userProfile)
			setIsAuthenticated(true)
			setError(null)
			isLoggingOutRef.current = false
		},
		onError: (error: LoginError) => {
			setError(error)
		}
	})

	const logoutMutation = useMutation({
		mutationFn: async () => {
			await AuthService.logout()
		},
		onSuccess: () => {
			setUserProfile(null)
			setIsAuthenticated(false)
			AuthService.clearSession()
			queryClient.invalidateQueries({ predicate: (_query) => true })
			queryClient.cancelQueries({ predicate: (_query) => true })
		},
		onError: (error: LoginError) => {
			setError(error)
		}
	})

	const isIdle = useIdle(IDLE_TIMEOUT_SECONDS, { initialState: false })

	const refreshSession = async (token: string, idToken?: string) => {
		try {
			if (!token) {
				throw new Error("No valid token provided")
			}

			AuthService.setAuthToken(token, idToken || "")

			// The csrf-token endpoint is authenticated, so fetch the tokens
			// only after the auth token has been set.
			await initializeCsrf()

			const userProfile = await AuthService.fetchProfile()
			setUserProfile(userProfile)
			setIsAuthenticated(true)
			setError(null)
			isLoggingOutRef.current = false
		} catch (error) {
			setError(error as LoginError)
			setIsAuthenticated(false)
			AuthService.clearSession()
		}
	}

	const dismissNotification = async (
		notification: DismissedNotificationRequest
	) => {
		try {
			createDismissedNotification({
				start: notification.start,
				end: notification.end,
				notificationId: notification.notificationId
			}).then(async () => {
				const updatedProfile = await refreshUserProfile()
				setUserProfile(updatedProfile)
			})
		} catch (_error) {
			// Error dismissing notification
		}
	}

	const refreshUserProfile = useCallback(async (): Promise<UserProfile> => {
		const profile = await AuthService.fetchProfile()
		setUserProfile(profile)
		return profile
	}, [setUserProfile])

	const forceRefreshToken = useCallback(async (): Promise<void> => {
		if (USE_FORGEROCK) {
			// Silent renewal via the OAuth refresh_token grant (no redirect or
			// iframe). refreshTokens already persists the new tokens; it returns
			// null when no refresh token is available (scope lacks
			// offline_access), in which case there is nothing to renew here.
			await ForgeRockAuthService.refreshTokens()
			return
		}

		const { access_token, refresh_token, id_token } =
			await AuthService.refreshAccessToken()
		AuthService.setAuthToken(access_token, id_token, refresh_token)
	}, [])

	const checkLegacyToken = useCallback(async (): Promise<boolean> => {
		const urlParams = new URLSearchParams(window.location.search)
		const legacyToken = urlParams.get("z")

		if (legacyToken) {
			try {
				const result = await AuthService.validateLegacyToken(legacyToken)
				if (result?.access_token && result?.id_token) {
					await refreshSession(result.access_token, result.id_token)
					urlParams.delete("z")
					const cleanUrl =
						window.location.pathname +
						(urlParams.toString() ? `?${urlParams.toString()}` : "")
					window.history.replaceState(null, "", cleanUrl)
					return true
				}
			} catch (_error) {
				// Failed to validate legacy token
			}
		}
		return false
	}, [refreshSession])

	const handleLogout = useCallback(async () => {
		if (isLoggingOutRef.current) {
			return
		}

		isLoggingOutRef.current = true

		// Immediately clear the idle interval to prevent retriggering
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
			idleTimerRef.current = 0
		}

		setIsAuthenticated(false)

		try {
			const legacyToken = new URLSearchParams(window.location.search).get("z")
			if (legacyToken) {
				const hasValidLegacyToken = await checkLegacyToken()
				if (hasValidLegacyToken) {
					isLoggingOutRef.current = false
					return
				}
			}

			closeMenu()
			const hasUserNavigatedToLegacy = secureLocalStorage.getItem(
				StorageKeys.HAS_USER_NAVIGATED_TO_LEGACY
			)

			if (hasUserNavigatedToLegacy) {
				let ltpaTokenDeleted = false
				const cookies = document.cookie.split("; ")
				for (const cookie of cookies) {
					if (cookie.startsWith("LtpaToken")) {
						const cookieName = cookie.split("=")[0]
						document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure;`
						ltpaTokenDeleted = true
					}
				}
				if (!ltpaTokenDeleted) {
					secureLocalStorage.removeItem(
						StorageKeys.HAS_USER_NAVIGATED_TO_LEGACY
					)
					window.location.href = `${BASE_LEGACY_URL_LOGOUT}`
				}
			}

			// Shutdown WebChat if it exists
			if (window.Pypestream) {
				// Call shutdown first
				const pypestream = window.Pypestream
				pypestream.shutdown()

				// Clean up the Pypestream instance from the window object
				// We need to use 'any' here because we're intentionally setting a required property to undefined
				// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a required property to undefined
				;(window as any).Pypestream = undefined
			}

			// Clean up the wcwConfig from the window object
			// We need to use 'any' here to access a dynamically added property
			// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to access a dynamically added property
			if ((window as any).wcwConfig) {
				// We need to use 'any' here because we're intentionally setting a property to undefined
				// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a property to undefined
				;(window as any).wcwConfig = undefined
			}

			// Clean up the WebChatWidgetGlobals from the window object
			// We need to use 'any' here to access a dynamically added property
			// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to access a dynamically added property
			if ((window as any).WebChatWidgetGlobals) {
				// We need to use 'any' here because we're intentionally setting a property to undefined
				// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a property to undefined
				;(window as any).WebChatWidgetGlobals = undefined
			}

			// Remove WebChat container
			const wcwContainer = document.getElementById("wcwContainer")
			if (wcwContainer) {
				document.body.removeChild(wcwContainer)
			}

			if (USE_FORGEROCK) {
				// --- FORGEROCK LOGOUT FLOW ---
				try {
					// 1. Get ID Token for OIDC logout (before clearing session)
					const idToken = AuthService.getIdToken()

					// 2. Clear SDK State (Tokens)
					await ForgeRockAuthService.logout()

					// 3. Clear Local App Session
					AuthService.clearSession()
					setUserProfile(null)

					// 4. Perform OIDC Logout (Redirect)
					if (idToken) {
						await oauthService.logout(
							idToken,
							`${window.location.origin}/login`
						)
						return
					}
				} catch (_error) {
					AuthService.clearSession()
					setUserProfile(null)
				}
				navigate("/login")
			} else {
				// --- LEGACY LOGOUT FLOW ---
				try {
					await logoutMutation.mutateAsync()
				} catch (_error) {
					AuthService.clearSession()
					setUserProfile(null)
				}
				navigate("/login")
			}
		} catch (_error) {
			isLoggingOutRef.current = false
			AuthService.clearSession()
			setUserProfile(null)
			navigate("/login")
		}
	}, [
		logoutMutation,
		handleRedirect,
		closeMenu,
		navigate,
		checkLegacyToken,
		setUserProfile
	])

	useEffect(() => {
		// Don't run idle logout logic if already logging out
		if (isLoggingOutRef.current) {
			return
		}

		if (isAuthenticated) {
			const lastActive = secureLocalStorage.getItem(
				StorageKeys.LAST_ACTIVE_TIME
			)
			if (
				lastActive &&
				Date.now() - Number.parseInt(lastActive as string) >
					IDLE_TIMEOUT_SECONDS
			) {
				;(async () => {
					await handleLogout()
				})()
				return
			}

			if (isIdle) {
				idleTimerRef.current = 0
				intervalRef.current = setInterval(async () => {
					if (isLoggingOutRef.current) {
						if (intervalRef.current) {
							clearInterval(intervalRef.current)
							intervalRef.current = null
						}
						return
					}
					idleTimerRef.current += 1
					if (idleTimerRef.current >= 5) {
						await handleLogout()
					}
				}, 1000)
			} else {
				secureLocalStorage.setItem(
					StorageKeys.LAST_ACTIVE_TIME,
					Date.now().toString()
				)
				if (intervalRef.current) {
					clearInterval(intervalRef.current)
					intervalRef.current = null
					idleTimerRef.current = 0
				}
			}
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}
	}, [isIdle, isAuthenticated, handleLogout])

	useEffect(() => {
		const initializeProfile = async () => {
			const token = AuthService.getAuthToken()

			if (token) {
				try {
					// The csrf-token endpoint is authenticated, so fetch the tokens
					// for the already-authenticated (e.g. reloaded) session.
					await initializeCsrf()

					const profile = await AuthService.fetchProfile()

					const tokenPayload = AuthService.decodeJWTToken(token)

					// For JWE tokens, skip username validation and trust the stored token
					if (tokenPayload !== null) {
						const tokenUsername =
							AuthService.extractUsernameFromToken(tokenPayload)

						if (tokenUsername && tokenUsername === profile?.loginUserName) {
							setUserProfile(profile)
							setIsAuthenticated(true)
						} else {
							setIsAuthenticated(false)
							setUserProfile(null)
							AuthService.clearSession()
						}
					} else {
						// JWE token - trust stored token and proceed with profile
						setUserProfile(profile)
						setIsAuthenticated(true)
					}
				} catch {
					setIsAuthenticated(false)
					setUserProfile(null)
					AuthService.clearSession()
				}
			} else {
				setIsAuthenticated(false)
				setUserProfile(null)
			}
			setIsLoading(false)
		}

		initializeProfile().catch(() => {
			setIsLoading(false)
		})

		const handleCustomEvent = (event: CustomEvent) => {
			if (event.detail.key === StorageKeys.USER_PROFILE) {
				try {
					const updatedProfile = JSON.parse(event.detail.newValue || "{}")
					setUserProfile(updatedProfile)
				} catch (_error) {
					// Error parsing user profile
				}
			}
		}

		window.addEventListener(
			"localstorage-change",
			handleCustomEvent as EventListener
		)

		return () => {
			window.removeEventListener(
				"localstorage-change",
				handleCustomEvent as EventListener
			)
		}
	}, [])

	// Minimal ForgeRock login handler
	const loginWithForgeRock = useCallback(
		async (userInfo: UserInfo): Promise<void> => {
			try {
				if (userInfo) {
					const adaptedProfile = adaptForgeRockUser(userInfo)
					setUserProfile(adaptedProfile)
					setIsAuthenticated(true)
					setError(null)
				}
			} catch (error) {
				setError(error as LoginError)
			}
		},
		[setUserProfile, setIsAuthenticated, setError]
	)

	return (
		<AuthContext.Provider
			value={{
				userProfile,
				setUserProfile,
				isAuthenticated,
				isLoggingOut: isLoggingOutRef.current,
				isLoading:
					isLoading ||
					loginMutation.status === PENDING ||
					logoutMutation.status === PENDING,
				login: loginMutation.mutateAsync,
				postLogin: postLoginMutation.mutateAsync,
				logout: handleLogout,
				error: (error ||
					loginMutation.error ||
					logoutMutation.error) as LoginError,
				dismissNotification,
				refreshUserProfile,
				refreshSession,
				forceRefreshToken,
				loginWithForgeRock: USE_FORGEROCK ? loginWithForgeRock : undefined
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}
